/**
 * Wraps the api() helper with Clerk's session token so authenticated routes
 * just work. Re-fetches the token per call — Clerk caches them internally so
 * this is cheap.
 *
 * Stale-session handling: on a long-open tab the cached session token can be
 * dead by the time we use it, so an authed call comes back 401. We retry once
 * with a force-refreshed token (skipCache); if that still 401s, the session is
 * genuinely gone, so we sign out to reset the app to a clean signed-out state
 * (the topbar's Sign in button reappears) rather than leaving callers wedged
 * on "Loading…" forever.
 */

import { useAuth } from '@clerk/react';
import { useCallback } from 'react';
import { api } from './api';

function statusOf(e: unknown): number | undefined {
  return (e as Error & { status?: number })?.status;
}

export function useAuthedApi() {
  const { getToken, signOut } = useAuth();
  return useCallback(
    async <T>(
      path: string,
      opts: { method?: string; body?: unknown } = {},
    ): Promise<T> => {
      try {
        const token = await getToken();
        return await api<T>(path, { ...opts, token });
      } catch (err) {
        if (statusOf(err) !== 401) throw err;
        // Token was likely stale. Force a fresh one and retry once.
        let token: string | null;
        try {
          token = await getToken({ skipCache: true });
        } catch {
          token = null;
        }
        try {
          return await api<T>(path, { ...opts, token });
        } catch (retryErr) {
          if (statusOf(retryErr) === 401) {
            // Session is genuinely dead — reset to a clean signed-out state.
            await signOut();
          }
          throw retryErr;
        }
      }
    },
    [getToken, signOut],
  );
}
